/** Usage marks ("do I actually use this?") with validated localStorage persistence.
 *
 * Backed by a Map so merchant-derived keys like "__proto__" are plain data,
 * never object properties. Deserialization validates every entry.
 */

import type { UsageMark } from '../lib/types';

const STORE_KEY = 'subradar.usage.v2';
const VALID_MARKS: readonly UsageMark[] = ['keep', 'rarely', 'never'];
const MAX_KEY_LENGTH = 200;
const MAX_ENTRIES = 5_000;

function isUsageMark(v: unknown): v is UsageMark {
  return typeof v === 'string' && (VALID_MARKS as readonly string[]).includes(v);
}

export class UsageStore {
  private marks = new Map<string, UsageMark>();

  constructor(private storage: Storage | null = defaultStorage()) {
    this.load();
  }

  get(key: string): UsageMark {
    return this.marks.get(key) ?? 'keep';
  }

  set(key: string, mark: UsageMark): void {
    if (key.length > MAX_KEY_LENGTH || this.marks.size >= MAX_ENTRIES) return;
    if (mark === 'keep') this.marks.delete(key);
    else this.marks.set(key, mark);
    this.persist();
  }

  clear(): void {
    this.marks.clear();
    this.storage?.removeItem(STORE_KEY);
  }

  get size(): number {
    return this.marks.size;
  }

  private load(): void {
    const raw = this.storage?.getItem(STORE_KEY);
    if (!raw) return;
    try {
      const parsed: unknown = JSON.parse(raw);
      if (!Array.isArray(parsed)) return;
      for (const entry of parsed.slice(0, MAX_ENTRIES)) {
        if (
          Array.isArray(entry) &&
          typeof entry[0] === 'string' &&
          entry[0].length <= MAX_KEY_LENGTH &&
          isUsageMark(entry[1]) &&
          entry[1] !== 'keep'
        ) {
          this.marks.set(entry[0], entry[1]);
        }
      }
    } catch {
      // Corrupt store: start fresh rather than crash.
      this.storage?.removeItem(STORE_KEY);
    }
  }

  private persist(): void {
    try {
      this.storage?.setItem(STORE_KEY, JSON.stringify([...this.marks.entries()]));
    } catch {
      // Quota exceeded or storage unavailable — marks stay in-memory only.
    }
  }
}

function defaultStorage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null; // storage blocked (e.g. cookies disabled)
  }
}
